import type { Attachment, UIMessage } from "ai";
import { AnimatePresence, motion } from "framer-motion";
import {
  type Dispatch,
  memo,
  type SetStateAction,
  useState,
  useCallback,
  useSyncExternalStore,
  type MutableRefObject,
  useEffect,
  useRef,
} from "react";
import useSWR from "swr";
import type { Document, Vote } from "@/lib/db/schema";
import { fetcher } from "@/lib/utils";
import { MultimodalInput } from "./multimodal-input";
import type { AgentModelKey } from "@/lib/ai/agent-models";
import { ArtifactMessages } from "./artifact-messages";
import { useArtifact } from "@/hooks/use-artifact";
import { WorkflowArtifact } from "@/components/workflow/WorkflowArtifact";
import { AlertCanvas } from "@/components/alert-canvas/AlertCanvas";
import { AlertDetailShell } from "@/components/alert-page/AlertDetailShell";
import { ContextualChatDock } from "@/components/contextual-chat-dock/ContextualChatDock";
import type { PageContextPayload } from "@/lib/page-context-types";
import { DocumentSkeleton } from "@/components/document-skeleton";
import equal from "fast-deep-equal";
import type { UseChatHelpers } from "@ai-sdk/react";
import type { RequestedDataSource } from "@/lib/data-catalog";
import type { VisibilityType } from "./visibility-selector";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  X,
  Workflow,
  Hammer,
  MessageSquare,
  ChevronLeft,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { ChatHeader } from "@/components/chat-header";
import { ReplayControls } from "@/components/replay-controls";
import { PostReplayInput } from "@/components/post-replay-input";
import { DemoReplayInput } from "@/components/demo-replay-input";
import { useDemoReplayInputEnabled } from "@/lib/demo-replay-config";
import { createChannelLogger } from "@/lib/debug-log";
import { telemetry } from "@/lib/telemetry";

const timingLog = createChannelLogger("alert_timing");
function tSinceNav(): number {
  if (typeof performance === "undefined") return -1;
  const navs = performance.getEntriesByType?.("navigation") as PerformanceNavigationTiming[] | undefined;
  const navStart = navs?.[0]?.startTime ?? 0;
  return Math.round(performance.now() - navStart);
}

const ALERT_VISITOR_PLACEHOLDERS = [
  "what does this alert do?",
  "how can I use this alert?",
  "walk me through each step",
  "what does each node mean?",
  "how should I read this signal?",
  "how often does this alert run?",
];

const ALERT_VISITOR_PROMPTS = [
  "What does this alert do?",
  "How can I use this alert?",
  "Explain each step",
  "How should I read this signal?",
  "What does each node mean?",
];

export type ArtifactKind = "text" | "image";
export interface ArtifactCellTarget {
  cellId?: string | null;
  cellIndex?: number | null;
  requestedAt: number;
}

export interface UIArtifact {
  title: string;
  documentId: string;
  kind: ArtifactKind;
  content: string;
  /** Legacy binary "is the artifact panel open at all". Kept for external
   *  call-sites, but internal layout now uses `isChatMinimized` +
   *  `isArtifactMinimized` to support three states
   *  (chat-full / split / artifact-full) without ever remounting <Artifact>. */
  isVisible: boolean;
  hideSteps: boolean;
  status: "streaming" | "idle";
  boundingBox: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  workflowView?: "current-run" | "summary" | "dashboard" | null;
  pendingCellTarget?: ArtifactCellTarget | null;
  /** When true, the chat column inside <Artifact> is collapsed to width 0.
   *  Combined with isArtifactMinimized=false this is "artifact-full" mode. */
  isChatMinimized?: boolean;
  /** When true, the artifact column inside <Artifact> is collapsed to width 0.
   *  Combined with isChatMinimized=false this is "chat-full" mode — the default
   *  for /chat/[id] routes. Always false on /alerts/[id]. */
  isArtifactMinimized?: boolean;
}

// Media query-backed mobile detector (no useEffect, SSR-safe)
function useIsMobile(): boolean {
  const subscribe = useCallback((onChange: () => void) => {
    if (typeof window === "undefined") return () => {};
    const mql = window.matchMedia("(max-width: 767px)");
    // Support older Safari
    const add = (mql as any).addEventListener
      ? "addEventListener"
      : "addListener";
    const remove = (mql as any).removeEventListener
      ? "removeEventListener"
      : "removeListener";
    (mql as any)[add]("change", onChange);
    return () => (mql as any)[remove]("change", onChange);
  }, []);

  const getSnapshot = useCallback(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  }, []);

  const getServerSnapshot = useCallback(() => false, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

function PureArtifact({
  chatId,
  viewMode = "research",
  alertStartCellIndex = null,
  input,
  setInput,
  handleSubmit,
  status,
  stop,
  attachments,
  setAttachments,
  append,
  messages,
  setMessages,
  reload,
  votes,
  isReadonly,
  readonlyReason,
  selectedVisibilityType,
  cellsExecuted = 0,
  dataRef,
  deepResearchRef,
  modelTierRef,
  modelTier,
  onModelTierChange,
  selectedModelKey,
  onModelChange,
  modelOverrideRef,
  subAgentMessageIdsRef,
  section,
  title,
  isDemo = false,
  isForkedWorkflow = false,
  replayId,
  replaySessionId,
  replayIsStreaming = false,
  replayNextUserMessage,
  onReplayContinue,
  replaySpeed,
  onReplaySpeedChange,
  replaySkipped,
  onReplaySkippedChange,
  replayPublishGate,
  onReplayPublishConfirm,
  replayCleanMode = false,
  alertHeader,
  hasAlertDoc = false,
  alertIsOwner = false,
  alertIsSubscribed = false,
  alertId = null,
  alertName = null,
  alertExplanationMd = null,
  alertScheduleText = null,
  alertNotificationEmail = null,
  isAlertsRoute = false,
  pageContextRef,
  cellDescriptions = null,
  alertPresentedBy = null,
  alertVisitorMode = false,
  onVisitorSubmitGate,
  /** When set (non-owner on /alerts/[id]), `AlertCanvas` fetches
   *  `/strategies/active-run-details` against THIS session id — the
   *  owner&apos;s `linked_session_id` with the real notebook cells. The
   *  chat `id` / `chatId` stays the visitor sandbox id for /agent/stream.
   *  Omit for owners and for normal /chat (defaults to `chatId`). */
  activeRunSessionId = null,
}: {
  chatId: string;
  viewMode?: string;
  /** Session whose PKA run/cells power the alert canvas. See `activeRunSessionId`. */
  activeRunSessionId?: string | null;
  alertStartCellIndex?: number | null;
  /** Optional alert-page header (e.g. <AlertTopBar>). When provided, it
   *  replaces the AlertCanvas's generic "Alert Canvas · ready" strip, so
   *  the header only spans the right-side canvas column — not the full
   *  width. Only the /alerts/{id} route passes this; /chat/{id} never
   *  does, keeping regular switch_mode("alert") behavior intact. */
  alertHeader?: React.ReactNode;
  /** True ONLY on the /alerts/[id] detail page. When true the AlertCanvas
   *  enables the icon-tab round-robin (3s staggered), the last-node
   *  right-column layout, and the water-ripple hover treatment. When false
   *  (i.e. /chat in alert mode), nodes render static with single-best-view
   *  so the user isn't distracted mid-research. */
  isAlertsRoute?: boolean;
  /** Per-cell markdown descriptions, keyed by FORK cell index (string).
   *  Sourced from `alertDoc.cell_descriptions` on the /alerts/[id] page.
   *  Passed through to <AlertCanvas> so every node's Summary tab can show
   *  the rich "What it does / What it produces" description without
   *  hitting the LLM at render time. Null when not available (e.g. legacy
   *  alerts mid-backfill, or /chat route). */
  cellDescriptions?: Record<string, string> | null;
  /** True when rendered under /alerts/[id] (i.e. parent <Chat> has an
   *  alertDoc). Used to swap the full-width "Find winning trades" landing
   *  hero for a compact alert-specific empty state when the session has
   *  zero messages. /chat/[id] always passes false. */
  hasAlertDoc?: boolean;
  /** True when the current user is the owner of the alert. Gates the
   *  floating MultimodalInput + hides the left peek tab. Non-owners
   *  never see any chat input — just the canvas + read-only top bar.
   *  TODO(future): allow non-owners to fork the alert into their own
   *  account from this view. */
  alertIsOwner?: boolean;
  /** True when the non-owner caller has an active subscription. The
   *  canvas flips the last-cell output from the "first snapshot" (stale
   *  DELAYED view) to the "latest snapshot" (LIVE view). */
  alertIsSubscribed?: boolean;
  /** The alert's _id. Passed down to AlertCanvas so it can fetch the
   *  right snapshot endpoint when rendering the "Final Results" node.
   *  `null` when we're in /chat alert-mode (no alert doc). */
  alertId?: string | null;
  /** Human-readable alert name for contextual chat injection. */
  alertName?: string | null;
  /** Generated Description-tab markdown from the alert doc. */
  alertExplanationMd?: string | null;
  /** Plain-English schedule shown on the Description tab. */
  alertScheduleText?: string | null;
  /** Notification email shown on the Description tab. */
  alertNotificationEmail?: string | null;
  /** Ref filled by ContextualChatDock on submit; read by Chat.prepareRequestBody. */
  pageContextRef?: MutableRefObject<PageContextPayload | null>;
  /** Public byline payload from `GET /alerts/{id}`. Forwarded to AlertCanvas
   *  so it can inject the floating "Made by {creator}" node for curated
   *  alerts. Null / anonymous → no node added. Only the /alerts/[id] route
   *  populates this. */
  alertPresentedBy?: import("@/lib/api-client").PublicProfile | null;
  /** True when this Chat is a non-owner VISITOR session on /alerts/[id].
   *  In this mode we keep the floating input + message list visible (the
   *  visitor gets their own private chat about the alert) but their agent
   *  is sandboxed by the backend (alert-write tools pruned, system prompt
   *  forbids editing). The owner's chat history is NEVER shown — the
   *  visitor sees only their own conversation, starting empty. */
  alertVisitorMode?: boolean;
  /** Called RIGHT BEFORE a visitor submits a message via the floating
   *  input or the docked input. Return `true` to allow the submit (e.g.
   *  paid tier — proceed with the network call). Return `false` to swallow
   *  the submit (e.g. free tier — open the plans modal instead). Only used
   *  when `alertVisitorMode` is true; ignored otherwise. The gate runs
   *  synchronously so we can short-circuit `append` cleanly. */
  onVisitorSubmitGate?: () => boolean;
  input: string;
  setInput: UseChatHelpers["setInput"];
  status: UseChatHelpers["status"];
  stop: UseChatHelpers["stop"];
  attachments: Array<Attachment>;
  setAttachments: Dispatch<SetStateAction<Array<Attachment>>>;
  messages: Array<UIMessage>;
  setMessages: UseChatHelpers["setMessages"];
  votes: Array<Vote> | undefined;
  append: UseChatHelpers["append"];
  handleSubmit: UseChatHelpers["handleSubmit"];
  reload: UseChatHelpers["reload"];
  isReadonly: boolean;
  readonlyReason?: "breaking_change" | "ownership" | "replay";
  selectedVisibilityType: VisibilityType;
  cellsExecuted?: number;
  dataRef?: MutableRefObject<Array<RequestedDataSource>>;
  deepResearchRef?: MutableRefObject<boolean>;
  modelTierRef?: MutableRefObject<string>;
  modelTier?: "basic" | "max";
  onModelTierChange?: (tier: "basic" | "max") => void;
  selectedModelKey?: AgentModelKey;
  onModelChange?: (key: AgentModelKey) => void;
  modelOverrideRef?: MutableRefObject<string | undefined>;
  subAgentMessageIdsRef?: MutableRefObject<Set<string>>;
  section?: "chat";
  title?: string;
  isDemo?: boolean;
  isForkedWorkflow?: boolean;
  replayId?: string;
  replaySessionId?: string;
  replayIsStreaming?: boolean;
  replayNextUserMessage?: string | null;
  onReplayContinue?: (msg: string) => void;
  replaySpeed?: number;
  onReplaySpeedChange?: (speed: number) => void;
  replaySkipped?: boolean;
  onReplaySkippedChange?: (skipped: boolean) => void;
  // Publish gate (replay-only). Same shape as on ReplayControls;
  // see chat.tsx for how the value is derived from the SSE `data` array.
  replayPublishGate?: {
    alertName: string | null;
    scheduleDescription: string | null;
    toolCallId: string | null;
  } | null;
  onReplayPublishConfirm?: () => void;
  /**
   * Clean replay mode: forwarded into the canvas-mode
   * `<DemoReplayInput>` so it skips auto-prefill of the canned
   * next-user message and lets the viewer type their own continuation.
   * Also flips the render gate so the demo input shows for clean
   * replays even without the `useDemoReplayInputEnabled` flag.
   */
  replayCleanMode?: boolean;
}) {
  const { artifact, setArtifact } = useArtifact();
  const router = useRouter();
  const { user } = useAuth();
  // See `lib/demo-replay-config.ts`. When on, both replay-bar render
  // sites below swap their `<ReplayControls>` for a `<DemoReplayInput>`
  // (a real-looking chat input bar), keeping the canvas-mode replay
  // visually identical to the live product for screen recordings.
  const demoReplayInputOn = useDemoReplayInputEnabled();

  const _artifactMountLogged = useRef(false);
  if (hasAlertDoc && !_artifactMountLogged.current) {
    _artifactMountLogged.current = true;
    timingLog("artifact:first_call", { t: tSinceNav() });
  }

  // Client-side mobile detection via media query subscription (no useEffect). Preserves behavior.
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!hasAlertDoc && !artifact.isVisible) return;
    telemetry.emit("artifact.open", {
      session_id: chatId,
      user_id: user?.uid,
      kind: artifact.kind,
      has_alert_doc: hasAlertDoc,
      is_mobile: isMobile,
      is_visible: artifact.isVisible,
      is_chat_minimized: artifact.isChatMinimized ?? false,
      is_artifact_minimized: artifact.isArtifactMinimized ?? false,
    });
    telemetry.emit("alert_canvas.layout_state", {
      session_id: chatId,
      user_id: user?.uid,
      source: "artifact_render",
      view_mode: viewMode,
      has_alert_doc: hasAlertDoc,
      is_mobile: isMobile,
      is_visible: artifact.isVisible,
      is_chat_minimized: artifact.isChatMinimized ?? false,
      is_artifact_minimized: artifact.isArtifactMinimized ?? false,
    });
    if (
      (viewMode === "alert" || viewMode === "signal") &&
      !isMobile &&
      !hasAlertDoc &&
      (artifact.isChatMinimized ?? false)
    ) {
      telemetry.emit("alert_canvas.bad_state_detected", {
        session_id: chatId,
        user_id: user?.uid,
        reason: "desktop_chat_minimized_during_chat_alert_build",
        is_visible: artifact.isVisible,
        is_chat_minimized: artifact.isChatMinimized ?? false,
        is_artifact_minimized: artifact.isArtifactMinimized ?? false,
      });
    }
    if ((viewMode === "alert" || viewMode === "signal") && isMobile && !artifact.isVisible) {
      telemetry.emit("alert_canvas.bad_state_detected", {
        session_id: chatId,
        user_id: user?.uid,
        reason: "mobile_alert_mode_without_canvas",
        is_visible: artifact.isVisible,
        is_chat_minimized: artifact.isChatMinimized ?? false,
        is_artifact_minimized: artifact.isArtifactMinimized ?? false,
      });
    }
  }, [
    artifact.isArtifactMinimized,
    artifact.isChatMinimized,
    artifact.isVisible,
    chatId,
    hasAlertDoc,
    isMobile,
    user?.uid,
    viewMode,
  ]);

  // Per-render state snapshot for mobile debugging. Shows whether the chat
  // column (gated `!isMobile && (...)`), the canvas (gated `!isArtifactMinimized`),
  // and the mobile chat sheet (gated on messages > 0) are mounting on any
  // given render. If on mobile we see `chatColumnMounts: false` AND
  // `canvasMounts: false`, that's the "blank screen" bug.
  if (hasAlertDoc) {
    timingLog("artifact:render", {
      t: tSinceNav(),
      isMobile,
      isVisible: artifact.isVisible,
      isChatMinimized: artifact.isChatMinimized ?? false,
      isArtifactMinimized: artifact.isArtifactMinimized ?? false,
      chatColumnMounts: !isMobile,
      canvasMounts: !(artifact.isArtifactMinimized ?? false),
    });
  }

  // Track rerun state from WorkflowArtifact
  const [isRerunning, setIsRerunning] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  const handleRerunStatusChange = useCallback(
    (rerunning: boolean, initializing: boolean) => {
      setIsRerunning(rerunning);
      setIsInitializing(initializing);
    },
    []
  );

  // Track editing cell state
  const [editingCell, setEditingCell] = useState<{
    index: number;
    purpose: string;
  } | null>(null);

  const handleCellEdit = useCallback((index: number, purpose: string) => {
    setEditingCell({ index, purpose });
  }, []);

  const handleClearEditingCell = useCallback(() => {
    setEditingCell(null);
  }, []);

  // Layout state is now owned by the shared artifact store, so chat.tsx can
  // set sensible defaults per route (/chat/[id] → chat-full by default,
  // /alerts/[id] → artifact-full by default). Falls back to local defaults
  // so older callers of <Artifact> still work without touching the store.
  const isChatMinimized = artifact.isChatMinimized ?? false;
  const isArtifactMinimized = artifact.isArtifactMinimized ?? false;
  const setIsChatMinimized = useCallback(
    (next: boolean) =>
      setArtifact((prev) => ({ ...prev, isChatMinimized: next })),
    [setArtifact],
  );
  const setIsArtifactMinimized = useCallback(
    (next: boolean) =>
      setArtifact((prev) => ({ ...prev, isArtifactMinimized: next })),
    [setArtifact],
  );
  const [chatWidth, setChatWidth] = useState(384);

  // NOTE: previously here sat an `animationsEnabled` + double-rAF gate that
  // blocked Framer animations (and chat-column mount) for ~46ms after mount.
  // Its job was to hide a race: <Chat>'s mount useEffect would flip
  // `isChatMinimized=true` ONE tick AFTER the SWR-backed store's first
  // render, so Framer briefly saw width=384 → 0 and animated the chat column
  // sliding out. Now that /alerts/[id] pre-seeds the store in a
  // useLayoutEffect BEFORE <Chat> mounts, the store is already correct on
  // frame 1 — no race, no slide, no need to block. Removing the gate kills
  // a 46ms layout-shift window that users saw as "canvas shifts left, snaps
  // back right, weird container padding appears" on first load. Verified
  // via frontend_alert_page_flash.txt on 2026-04-20.

  // ── Alert-page: expand chat column when user submits from floating input
  // We wrap the floating <MultimodalInput>'s `append` prop directly (see
  // the JSX below) to flip `isChatMinimized=false` the instant the user
  // sends. This replaces a brittle message-count-watching effect that
  // raced with SWR revalidations and caused both inputs to briefly show
  // at once.

  const [replayFinished, setReplayFinished] = useState(false);
  const onReplayComplete = useCallback(() => {
    setReplayFinished(true);
  }, []);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);

  const handleRunStrategy = useCallback(() => {
    append({
      role: "user",
      content: "Run Strategy",
    });
  }, [append]);

  const handleAddStep = useCallback(
    (message: string) => {
      append({
        role: "user",
        content: message,
      });
    },
    [append]
  );

  const handleEditCell = useCallback(
    (cellId: string, message: string) => {
      append({
        role: "user",
        content: `Edit step ${cellId}: ${message}`,
      });
    },
    [append]
  );

  // Visitor-on-alert-page submit gate. Wraps `append` so that when the
  // current user is a non-owner viewing /alerts/[id], every submit goes
  // through `onVisitorSubmitGate()` first. The gate decides:
  //   • signed-in paid user → returns true → proceed normally
  //   • free user → returns false + opens PlansModal → swallow submit
  //   • signed-out user → returns false + opens AuthModal → swallow submit
  // For non-visitor flows (owners, regular /chat) the wrapper is a
  // pass-through, so this stays a one-line behavior change.
  const wrappedAppend = useCallback<UseChatHelpers["append"]>(
    (message, chatRequestOptions) => {
      if (alertVisitorMode && onVisitorSubmitGate) {
        const allowed = onVisitorSubmitGate();
        if (!allowed) {
          // Returning a resolved Promise<null> mirrors what useChat's
          // append returns when it bails out (e.g. when input is empty).
          // It keeps the MultimodalInput happy without sending a network
          // request. The gate has already opened the appropriate modal.
          return Promise.resolve(null);
        }
      }
      return append(message, chatRequestOptions);
    },
    [alertVisitorMode, onVisitorSubmitGate, append],
  );

  const submitFloatingAlertPrompt = useCallback(
    (content: string) => {
      if (alertVisitorMode && onVisitorSubmitGate) {
        const allowed = onVisitorSubmitGate();
        if (!allowed) {
          console.info(
            "[artifact] alert prompt pill BLOCKED by visitor gate (paywall)",
          );
          return Promise.resolve(null);
        }
      }

      console.info("[artifact] alert prompt pill → expanding chat column", {
        isMobile,
        alertVisitorMode,
      });
      setIsChatMinimized(false);
      if (isMobile) {
        setIsArtifactMinimized(true);
      }
      return append({ role: "user", content });
    },
    [
      alertVisitorMode,
      onVisitorSubmitGate,
      append,
      isMobile,
      setIsChatMinimized,
      setIsArtifactMinimized,
    ],
  );

  const renderArtifactContent = (isInline: boolean = false) => {
    if ((viewMode === "alert" || viewMode === "signal")) {
      const isStreamingNow = status === "streaming" || status === "submitted";
      const useAlertDetailShell = isAlertsRoute && Boolean(alertId) && Boolean(alertHeader);
      const canvas = (
        <AlertCanvas
          sessionId={activeRunSessionId ?? chatId}
          alertStartCellIndex={alertStartCellIndex ?? null}
          isStreaming={isStreamingNow}
          customHeader={useAlertDetailShell ? undefined : alertHeader}
          isAlertsRoute={isAlertsRoute}
          cellDescriptions={cellDescriptions}
          alertId={alertId ?? undefined}
          isOwner={alertIsOwner}
          isSubscribed={alertIsSubscribed}
          presentedBy={alertPresentedBy}
          viewMode={viewMode}
        />
      );

      if (useAlertDetailShell && alertId) {
        return (
          <AlertDetailShell
            alertId={alertId}
            header={alertHeader}
            canvas={canvas}
            isOwner={alertIsOwner}
            initialExplanationMd={alertExplanationMd}
            scheduleText={alertScheduleText}
            notificationEmail={alertNotificationEmail}
            viewMode={viewMode}
          />
        );
      }

      return canvas;
    }

    // Default: WorkflowArtifact for research mode
    return (
      <div className="h-[calc(100%-0.5rem)]">
        <WorkflowArtifact
          sessionId={chatId}
          onRunStrategy={handleRunStrategy}
          onAddStep={handleAddStep}
          onEditStep={(stepIndex, message) =>
            handleEditCell(stepIndex.toString(), message)
          }
          onEditCell={handleCellEdit}
          status={status}
          messages={messages}
          cellsExecuted={cellsExecuted}
          input={input}
          setInput={setInput}
          handleSubmit={handleSubmit}
          stop={stop}
          attachments={attachments}
          setAttachments={setAttachments}
          append={append}
          setMessages={setMessages}
          reload={reload}
          votes={votes}
          isReadonly={isReadonly}
          selectedVisibilityType={selectedVisibilityType}
          onRerunStatusChange={handleRerunStatusChange}
          isForkedWorkflow={isForkedWorkflow}
          isReplay={readonlyReason === "replay"}
        />
      </div>
    );
  };
  // Handle resize — use pointer events for reliable capture even over iframes
  const dragElementRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isDragging) return;

    const stopDrag = () => {
      setIsDragging(false);
      if (dragElementRef.current) {
        dragElementRef.current.releasePointerCapture?.(activePtrId.current);
      }
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = e.clientX - containerRect.left;
      const minWidth = 384;
      const maxWidth = containerRect.width * 0.5 - 32;
      setChatWidth(Math.min(Math.max(newWidth, minWidth), maxWidth));
    };

    const handlePointerUp = () => stopDrag();
    const handlePointerCancel = () => stopDrag();
    const handleBlur = () => stopDrag();
    const preventSelect = (e: Event) => e.preventDefault();

    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);
    document.addEventListener("pointercancel", handlePointerCancel);
    window.addEventListener("blur", handleBlur);
    document.addEventListener("selectstart", preventSelect);

    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
      document.removeEventListener("pointercancel", handlePointerCancel);
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("selectstart", preventSelect);
    };
  }, [isDragging]);
  const activePtrId = useRef<number>(0);

  // When the artifact column is minimized we want the chat to flex to fill the
  // whole width. Using `width: "100%"` instead of the fixed chatWidth gives us
  // the "chat-full" mode without remounting or changing components.
  const computedChatWidth = isArtifactMinimized ? "100%" : chatWidth;

  // Chat mode: render responsive layout
  return (
    <div
      data-testid="artifact"
      className="relative flex flex-col md:flex-row h-full"
      ref={containerRef}
    >
      {/* Chat column — desktop: side-by-side split with canvas.
          Mobile: mounted ONLY when the user is in chat mode
          (`!isChatMinimized`); in that case combined with
          `isArtifactMinimized: true` it takes the whole screen (the
          canvas motion.div collapses to width 0). This gives the
          mobile /alerts/[id] page a clean binary toggle — canvas mode
          (floating input only) ↔ full-screen chat mode — instead of a
          half-canvas / half-chat overlap that the previous
          "mobile chat sheet" approach (gated on messages.length > 0)
          was producing on every page load with historical messages.
          On desktop this is always mounted; width animates between 0
          and `computedChatWidth` via `isChatMinimized`. */}
      {(!isMobile || !isChatMinimized) && (
        <motion.div
          ref={chatRef}
          // Stable hook for buddy beats that need to spotlight the
          // entire right-side chat column (e.g. ask_user "quoting"
          // beats, where we want to dim only the canvas / left area
          // and keep the whole vertical chat strip bright). The
          // selector is documented in the buddy seed script.
          data-testid="alert-chat-column"
          className="flex flex-col h-full overflow-hidden"
          // On alert pages we FORCE the initial mount state to be the
          // minimized (0 width, 0 opacity) version so the chat column
          // never visually exists for a frame before the layout effect
          // flips the store. On /chat/[id] we keep the default behavior
          // (seamless mount at current width).
          initial={
            hasAlertDoc
              ? { width: 0, opacity: 0 }
              : { width: computedChatWidth, opacity: 1 }
          }
          animate={{
            width: isChatMinimized ? 0 : computedChatWidth,
            opacity: isChatMinimized ? 0 : 1,
          }}
          transition={{
            duration: isDragging ? 0 : 0.4,
            ease: "easeInOut",
          }}
          style={{ flexShrink: 0 }}
        >
          {/* ChatHeader - now inside resizable container */}
          {!isDemo && (
            <ChatHeader
              chatId={chatId}
              selectedVisibilityType={selectedVisibilityType}
              isReadonly={isReadonly}
              section="chat"
              title={title}
              messages={messages}
              hasMessages={messages.length > 0}
              append={append}
              viewMode={viewMode}
            />
          )}

          {messages.length === 0 ? (
            // Centered layout when no messages
            <div
              className={`flex-1 min-h-0 flex flex-col justify-center items-center ${
                isMobile ? "px-4 pt-0 pb-9" : "p-4"
              }`}
            >
              {/*   <div className="h-full flex flex-col justify-start items-center rounded-lg border border-border p-4 pt-36"> */}
              <div className="w-full">
                {/* Non-owner alert viewers normally don't see the owner's
                    build history. EXCEPTION: visitor mode — a non-owner
                    who clicked into the floating chat gets their OWN
                    private conversation rendered here (initialMessages
                    is empty in page.tsx for visitors, so this only
                    shows the visitor's own turns + agent replies — no
                    leak of owner content). */}
                {(!hasAlertDoc || alertIsOwner || alertVisitorMode) && (
                  <ArtifactMessages
                    chatId={chatId}
                    status={status}
                    votes={votes}
                    messages={messages}
                    setMessages={setMessages}
                    reload={reload}
                    isReadonly={isReadonly}
                    artifactStatus={artifact.status}
                    append={append}
                    subAgentMessageIdsRef={subAgentMessageIdsRef}
                    viewMode={viewMode}
                    alertStartCellIndex={alertStartCellIndex}
                    isAlertEmpty={hasAlertDoc && (viewMode === "alert" || viewMode === "signal")}
                  />
                )}

                {!isReadonly ? (
                  <form className="w-full mt-2">
                    <MultimodalInput
                      chatId={chatId}
                      input={input}
                      setInput={setInput}
                      handleSubmit={handleSubmit}
                      status={status}
                      stop={stop}
                      attachments={attachments}
                      setAttachments={setAttachments}
                      messages={messages}
                      append={wrappedAppend}
                      setMessages={setMessages}
                      selectedVisibilityType={selectedVisibilityType}
                      dataRef={dataRef}
                      deepResearchRef={deepResearchRef}
                      modelTierRef={modelTierRef}
                      modelTier={modelTier}
                      onModelTierChange={onModelTierChange}
                      selectedModelKey={selectedModelKey}
                      onModelChange={onModelChange}
                      modelOverrideRef={modelOverrideRef}
                      isRerunning={isRerunning}
                      isInitializing={isInitializing}
                      editingCell={editingCell}
                      onClearEditingCell={handleClearEditingCell}
                    />
                  </form>
                ) : readonlyReason === "replay" && replaySessionId && !replayFinished && (demoReplayInputOn || replayCleanMode) ? (
                  // Demo-recording mode: real-looking input bar replaces
                  // the replay strip in the canvas-mode chat panel.
                  // Same component used in chat.tsx's no-canvas branch.
                  <div className="w-full mt-2">
                    <DemoReplayInput
                      sessionId={replaySessionId}
                      curatedId={replayId}
                      // Multi-turn replay-gate plumbing. Without these,
                      // the canvas-mode <DemoReplayInput> never sees the
                      // canned `next_user_message` and the textarea
                      // stays empty / Send button never lights up.
                      // Same callback shape <ReplayControls>.onContinue
                      // uses below (chat.tsx wires this to advance the
                      // consumed-at cursor + append the user message).
                      nextUserMessage={replayNextUserMessage}
                      onContinueReplay={onReplayContinue}
                      // Clean replay: skip auto-prefill of the canned
                      // next-user message; viewer types freely.
                      disableAutoPrefill={replayCleanMode}
                    />
                  </div>
                ) : readonlyReason === "replay" && replaySessionId && !replayFinished ? (
                  <div className="w-full mt-2">
                    <ReplayControls
                      sessionId={replaySessionId}
                      isStreaming={replayIsStreaming}
                      nextUserMessage={replayNextUserMessage}
                      onContinue={onReplayContinue}
                      onComplete={onReplayComplete}
                      speed={replaySpeed}
                      onSpeedChange={onReplaySpeedChange}
                      isSkipped={replaySkipped}
                      onSkippedChange={onReplaySkippedChange}
                      replayQuery={title}
                    />
                  </div>
                ) : readonlyReason === "replay" && replayFinished && !user ? (
                  <div className="w-full mt-2">
                    <PostReplayInput replayQuery={title} />
                  </div>
                ) : (
                  <div className="w-full mt-2">
                    {readonlyReason === "breaking_change" ? (
                      <div className="flex flex-col gap-3">
                        <div className="relative text-center px-4 py-4 bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 rounded-lg border border-zinc-700 shadow-lg">
                          <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent rounded-lg pointer-events-none" />
                          <p className="relative text-base font-semibold text-foreground mb-2">
                            Hey{" "}
                            {user?.displayName?.split(" ")[0]
                              ? user.displayName
                                  .split(" ")[0]
                                  .charAt(0)
                                  .toUpperCase() +
                                user.displayName
                                  .split(" ")[0]
                                  .slice(1)
                                  .toLowerCase()
                              : "There"}{" "}
                            👋
                          </p>
                          <p className="relative text-sm text-zinc-400 leading-relaxed">
                            We launched Xynth 3.0 with major upgrades.
                            Unfortunately, breaking changes mean we can&apos;t
                            support old chats. We apologize for the
                            inconvenience but promise a much better experience!
                          </p>
                        </div>
                        <Button
                          onClick={() => router.push("/")}
                          className="w-full bg-gradient-to-r from-zinc-100 to-white hover:from-white hover:to-zinc-100 text-black font-semibold py-6 rounded-xl shadow-md hover:shadow-lg transition-all"
                        >
                          Start New Chat ✨
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={() => router.push("/")}
                        className="w-full bg-white text-black hover:bg-zinc-200 font-medium py-6 rounded-xl flex items-center justify-center gap-2"
                      >
                        <ArrowLeft size={20} />
                        Back
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Messages exist - normal scrollable layout
            <div
              className={`flex-1 min-h-0 flex flex-col ${
                isMobile ? "px-4 pt-2 pb-9" : "p-4"
              } relative`}
            >
              <div className="w-full flex flex-col flex-1 min-h-0">
                {/* Non-owner alert viewers normally don't see the owner's
                    build history. EXCEPTION: visitor mode (see sibling
                    branch for the same rationale). The visitor's
                    initialMessages is `[]` upstream, so this only ever
                    renders the visitor's own messages — never owner content. */}
                {(!hasAlertDoc || alertIsOwner || alertVisitorMode) && (
                  <div className="flex-1 min-h-0 mb-2">
                    <ArtifactMessages
                      chatId={chatId}
                      status={status}
                      votes={votes}
                      messages={messages}
                      setMessages={setMessages}
                      reload={reload}
                      isReadonly={isReadonly}
                      artifactStatus={artifact.status}
                      append={append}
                      subAgentMessageIdsRef={subAgentMessageIdsRef}
                      viewMode={viewMode}
                      alertStartCellIndex={alertStartCellIndex}
                      isAlertEmpty={hasAlertDoc && (viewMode === "alert" || viewMode === "signal")}
                    />
                  </div>
                )}

                {!isReadonly ? (
                  <form className="w-full">
                    <MultimodalInput
                      chatId={chatId}
                      input={input}
                      setInput={setInput}
                      handleSubmit={handleSubmit}
                      status={status}
                      stop={stop}
                      attachments={attachments}
                      setAttachments={setAttachments}
                      messages={messages}
                      append={wrappedAppend}
                      setMessages={setMessages}
                      selectedVisibilityType={selectedVisibilityType}
                      dataRef={dataRef}
                      deepResearchRef={deepResearchRef}
                      modelTierRef={modelTierRef}
                      modelTier={modelTier}
                      onModelTierChange={onModelTierChange}
                      selectedModelKey={selectedModelKey}
                      onModelChange={onModelChange}
                      modelOverrideRef={modelOverrideRef}
                      isRerunning={isRerunning}
                      isInitializing={isInitializing}
                      editingCell={editingCell}
                      onClearEditingCell={handleClearEditingCell}
                    />
                  </form>
                ) : readonlyReason === "replay" && replaySessionId && !replayFinished && (demoReplayInputOn || replayCleanMode) ? (
                  // Demo-recording mode — see the matching branch above.
                  // Two render sites because the alert-canvas chat panel
                  // is mounted twice in the artifact layout (with-feed
                  // and without-feed) and we want the demo bar in both.
                  <div className="w-full">
                    <DemoReplayInput
                      sessionId={replaySessionId}
                      curatedId={replayId}
                      // Multi-turn replay-gate plumbing. Without these,
                      // the canvas-mode <DemoReplayInput> never sees the
                      // canned `next_user_message` and the textarea
                      // stays empty / Send button never lights up.
                      // Same callback shape <ReplayControls>.onContinue
                      // uses below (chat.tsx wires this to advance the
                      // consumed-at cursor + append the user message).
                      nextUserMessage={replayNextUserMessage}
                      onContinueReplay={onReplayContinue}
                      // Clean replay: skip auto-prefill of the canned
                      // next-user message; viewer types freely.
                      disableAutoPrefill={replayCleanMode}
                    />
                  </div>
                ) : readonlyReason === "replay" && replaySessionId && !replayFinished ? (
                  <div className="w-full">
                    <ReplayControls
                      sessionId={replaySessionId}
                      isStreaming={replayIsStreaming}
                      nextUserMessage={replayNextUserMessage}
                      onContinue={onReplayContinue}
                      onComplete={onReplayComplete}
                      speed={replaySpeed}
                      onSpeedChange={onReplaySpeedChange}
                      isSkipped={replaySkipped}
                      onSkippedChange={onReplaySkippedChange}
                      replayQuery={title}
                      publishGate={replayPublishGate}
                      onPublishConfirm={onReplayPublishConfirm}
                    />
                  </div>
                ) : readonlyReason === "replay" && replayFinished && !user ? (
                  <div className="w-full">
                    <PostReplayInput replayQuery={title} />
                  </div>
                ) : (
                  <div className="w-full">
                    {readonlyReason === "breaking_change" ? (
                      <div className="flex flex-col gap-3">
                        <div className="relative text-center px-4 py-4 bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 rounded-lg border border-zinc-700 shadow-lg">
                          <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent rounded-lg pointer-events-none" />
                          <p className="relative text-base font-semibold text-foreground mb-2">
                            Hey{" "}
                            {user?.displayName?.split(" ")[0]
                              ? user.displayName
                                  .split(" ")[0]
                                  .charAt(0)
                                  .toUpperCase() +
                                user.displayName
                                  .split(" ")[0]
                                  .slice(1)
                                  .toLowerCase()
                              : "There"}{" "}
                            👋
                          </p>
                          <p className="relative text-sm text-zinc-400 leading-relaxed">
                            We launched Xynth 3.0 with major upgrades.
                            Unfortunately, breaking changes mean we can&apos;t
                            support old chats. We apologize for the
                            inconvenience but promise a much better experience!
                          </p>
                        </div>
                        <Button
                          onClick={() => router.push("/")}
                          className="w-full bg-gradient-to-r from-zinc-100 to-white hover:from-white hover:to-zinc-100 text-black font-semibold py-6 rounded-xl shadow-md hover:shadow-lg transition-all"
                        >
                          Start New Chat ✨
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={() => router.push("/")}
                        className="w-full bg-white text-black hover:bg-zinc-200 font-medium py-6 rounded-xl flex items-center justify-center gap-2"
                      >
                        <ArrowLeft size={20} />
                        Back
                      </Button>
                    )}
                  </div>
                )}
              </div>

            </div>
          )}
        </motion.div>
      )}

      {/* Draggable divider + chat/artifact toggle buttons - Desktop only.
          Only rendered when BOTH columns exist (i.e. split mode). In
          chat-full (artifact collapsed) or artifact-full (chat collapsed)
          mode we hide the divider entirely and show a small floating
          expand-tab on the collapsed edge instead — see the tabs below. */}
      {!isMobile && !isArtifactMinimized && !isChatMinimized && (
        <div
          ref={dragElementRef}
          className="w-1 bg-transparent relative group flex-shrink-0"
          style={{ userSelect: "none", cursor: "col-resize", touchAction: "none" }}
          onPointerDown={(e) => {
            activePtrId.current = e.pointerId;
            e.currentTarget.setPointerCapture(e.pointerId);
            setIsDragging(true);
          }}
        >
          <div className="absolute inset-y-0 -left-1 -right-1" />
          {/* Hover grey background - full height */}
          <div className="absolute inset-0 -left-1 -right-1 group-hover:bg-muted transition-colors" />
          {/* Permanent white line - full height */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-px h-full bg-white" />
          </div>
          {/* Visual indicator on hover */}
          <div className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <div className="w-1 h-12 bg-primary/70 rounded-full" />
          </div>
        </div>
      )}

      {/* LEFT EXPAND TAB — shown when chat is minimized (artifact-full).
          Click it to slide the chat back in.
          HIDDEN on ANY alert page (owner AND non-owner):
            • Owner: the floating MultimodalInput below is the primary
              re-entry point — no need for a peek tab.
            • Non-owner: they have no chat at all, so a "show chat" tab
              would be a trap that opens an empty drawer.
          Previously only `hasAlertDoc && alertIsOwner` was excluded, which
          leaked the tab to non-owners. Fixed by widening to `hasAlertDoc`. */}
      {!isMobile && isChatMinimized && !hasAlertDoc && viewMode !== "alert" && viewMode !== "signal" && (
        <div className="absolute top-1/2 -translate-y-1/2 left-0 z-50">
          <button
            onClick={() => {
              console.info("[artifact] expand chat (chat-minimized → split)");
              setIsChatMinimized(false);
            }}
            className="flex items-center justify-center w-6 h-16 rounded-r-lg bg-zinc-800 border border-zinc-700 border-l-0 hover:bg-zinc-700 transition-colors"
            title="Show chat"
          >
            <MessageSquare className="w-3.5 h-3.5 text-zinc-400" />
          </button>
        </div>
      )}

      {/* Contextual chat pill → expand composer (screenshot + page context on send). */}
      {(viewMode === "alert" || viewMode === "signal") &&
        isChatMinimized &&
        (!hasAlertDoc || alertIsOwner || alertVisitorMode) &&
        pageContextRef && (
          <ContextualChatDock
            chatId={chatId}
            input={input}
            setInput={setInput}
            handleSubmit={handleSubmit}
            status={status}
            stop={stop}
            attachments={attachments}
            setAttachments={setAttachments}
            messages={messages}
            append={wrappedAppend}
            setMessages={setMessages}
            selectedVisibilityType={selectedVisibilityType}
            dataRef={dataRef}
            deepResearchRef={deepResearchRef}
            modelTierRef={modelTierRef}
            modelTier={modelTier}
            onModelTierChange={onModelTierChange}
            selectedModelKey={selectedModelKey}
            onModelChange={onModelChange}
            modelOverrideRef={modelOverrideRef}
            isRerunning={isRerunning}
            isInitializing={isInitializing}
            editingCell={editingCell}
            onClearEditingCell={handleClearEditingCell}
            isMobile={isMobile}
            isAlertsRoute={isAlertsRoute}
            alertId={alertId}
            alertName={alertName}
            alertIsOwner={alertIsOwner}
            alertVisitorMode={alertVisitorMode}
            viewMode={viewMode}
            onVisitorSubmitGate={onVisitorSubmitGate}
            // In a live build session (no published alert doc yet) the real
            // chat column exists and was just minimized to show the canvas —
            // so the pill should re-open THAT chat, not spawn a separate
            // floating mini-composer. On the published alert page (hasAlertDoc)
            // there's no live thread, so keep the floating composer.
            onPillClick={
              !hasAlertDoc
                ? () => {
                    setIsChatMinimized(false);
                    if (isMobile) setIsArtifactMinimized(true);
                  }
                : undefined
            }
            pageContextRef={pageContextRef}
            placeholderTexts={
              alertVisitorMode ? ALERT_VISITOR_PLACEHOLDERS : undefined
            }
            promptPills={alertVisitorMode ? ALERT_VISITOR_PROMPTS : undefined}
            onExpandChat={() => {
              console.info(
                "[artifact] contextual dock submit → expanding chat column",
                { isMobile, alertVisitorMode },
              );
              setIsChatMinimized(false);
              if (isMobile) {
                setIsArtifactMinimized(true);
              }
            }}
          />
        )}

      {/* NOTE: the previous "mobile chat sheet" (absolute-positioned bottom
          overlay gated on `messages.length > 0`) was removed. Its gate
          fired on every page load for alerts with historical messages,
          causing a half-canvas / half-chat overlap ("the half part of
          the chat still showing"). The chat column above (now unlocked
          on mobile via `!isMobile || !isChatMinimized`) fully replaces
          it — on mobile chat mode it mounts at 100% width and the canvas
          motion.div collapses via `isArtifactMinimized: true`. */}

      {/* Artifact content - Full screen on mobile, right side on desktop.
          On /alerts/[id] we skip the slide-in animation entirely — the canvas
          IS the whole page, there's no chat column sliding the canvas into
          view. Starting at x:50 opacity:0 caused a visible right→left snap
          on first load even though the user never clicked to open anything.
          On /chat/[id] we keep the original animation because the artifact
          panel is genuinely opening from the right when the user clicks
          "View Full Analysis". */}
      <motion.div
        data-tour="artifact"
        className="flex-1 flex flex-col h-full relative min-w-0 overflow-hidden"
        initial={
          isAlertsRoute || isMobile
            ? { opacity: 1, x: 0 }
            : { opacity: 0, x: 50 }
        }
        animate={{
          opacity: isArtifactMinimized ? 0 : 1,
          width: isArtifactMinimized ? 0 : undefined,
          x: 0,
        }}
        transition={
          isAlertsRoute
            ? { duration: 0 }
            : !isMobile
              ? { duration: 0.4, ease: "easeInOut" }
              : { duration: 0 }
        }
        style={{
          // When minimized we also collapse flex-basis so the chat column
          // can take 100% width. flex:1 keeps the nice natural split when
          // visible.
          flex: isArtifactMinimized ? "0 0 0" : "1 1 0",
        }}
      >
        {/* Artifact content wrapper. On /alerts/[id] we drop the horizontal
            + top padding + rounded corners so the AlertCanvas renders
            edge-to-edge. /chat/[id] keeps the framed card padding so the
            artifact reads as a distinct panel beside the chat column. */}
        <div
          className={`
            ${isAlertsRoute ? "h-full" : "h-full px-4 pt-2 rounded-lg"}
          `}
        >
          {renderArtifactContent(false)}
        </div>
      </motion.div>

      {/* RIGHT EXPAND TAB — shown when artifact is minimized (chat-full) but
          only if we're in a viewMode that actually HAS an artifact to show.
          For plain research chats with an empty session we don't want a
          random tab floating there.

          Mobile behavior: on /alerts/[id] mobile, after the user submits
          their first message the chat takes the whole screen. This tab
          becomes their "back to canvas" button. Clicking it:
            1. Un-minimizes the artifact (canvas visible again).
            2. Also re-minimizes the chat column so the floating input
               returns (instead of landing in a split view the user then
               has to manually collapse). This matches the "floating ↔
               docked chat" binary state we use on the alert page. On
               regular /chat/[id] with switch_mode("alert"), hasAlertDoc
               is false so we skip the auto re-minimize and keep today's
               behavior. */}
      {/* Owner-only: non-owners never minimize the artifact (no chat
          column to pull over), so this re-expand tab is dead UI for
          them and previously leaked into the public view. */}
      {isMobile && isArtifactMinimized && (viewMode === "alert" || viewMode === "signal") && (!hasAlertDoc || alertIsOwner) && (
        <div data-tour="alert-mobile-back-to-canvas" className="absolute top-1/2 -translate-y-1/2 right-0 z-50">
          <button
            onClick={() => {
              console.info(
                "[artifact] expand artifact (artifact-minimized → alert page canvas)"
              );
              setIsArtifactMinimized(false);
              if (isMobile || (hasAlertDoc && alertIsOwner)) {
                // Alert page: collapse chat so floating input returns.
                setIsChatMinimized(true);
              }
            }}
            className="flex items-center justify-center w-6 h-16 rounded-l-lg bg-zinc-800 border border-zinc-700 border-r-0 hover:bg-zinc-700 transition-colors"
            title="Show alert canvas"
          >
            <MessageSquare className="w-3.5 h-3.5 text-zinc-400 rotate-180" />
          </button>
        </div>
      )}

      {/* LEFT OPEN-CHAT TAB — mobile + alerts-page only.
          Symmetric counterpart to the right "back to canvas" tab above:
          when the user is in CANVAS mode on mobile, this tab stuck to the
          left edge lets them jump straight into full-screen chat without
          having to tap the floating input first. Clicking it fires the
          SAME state flip the floating-input `append` handler uses —
          `isChatMinimized=false` + `isArtifactMinimized=true` — so the
          chat column mounts at 100% width and the canvas collapses.
          Gated to mobile only because desktop already shows the chat
          column side-by-side (no peek-tab needed) and to alert owners
          only because read-only viewers have no chat to open. */}
      {isMobile &&
        !isArtifactMinimized &&
        (viewMode === "alert" || viewMode === "signal") &&
        (!hasAlertDoc || alertIsOwner || alertVisitorMode) && (
          <div data-tour="alert-mobile-open-chat" className="absolute top-1/2 -translate-y-1/2 left-0 z-50">
            <button
              onClick={() => {
                console.info(
                  "[artifact] left peek tab → open chat (mobile alert page)"
                );
                setIsChatMinimized(false);
                setIsArtifactMinimized(true);
              }}
              className="flex items-center justify-center w-6 h-16 rounded-r-lg bg-zinc-800 border border-zinc-700 border-l-0 hover:bg-zinc-700 transition-colors"
              title="Open chat"
            >
              <MessageSquare className="w-3.5 h-3.5 text-zinc-400" />
            </button>
          </div>
        )}

    </div>
  );
}

export const Artifact = memo(PureArtifact, (prevProps, nextProps) => {
  if (prevProps.status !== nextProps.status) return false;
  if (!equal(prevProps.votes, nextProps.votes)) return false;
  if (prevProps.input !== nextProps.input) return false;
  if (!equal(prevProps.messages, nextProps.messages.length)) return false;
  if (prevProps.selectedVisibilityType !== nextProps.selectedVisibilityType)
    return false;
  if (prevProps.readonlyReason !== nextProps.readonlyReason) return false;
  if (prevProps.replayIsStreaming !== nextProps.replayIsStreaming) return false;
  if (prevProps.replayNextUserMessage !== nextProps.replayNextUserMessage)
    return false;
  if (prevProps.replaySpeed !== nextProps.replaySpeed) return false;
  if (prevProps.replaySkipped !== nextProps.replaySkipped) return false;
  if (prevProps.replayPublishGate !== nextProps.replayPublishGate) return false;
  if (prevProps.modelTier !== nextProps.modelTier) return false;
  if (prevProps.selectedModelKey !== nextProps.selectedModelKey) return false;
  if (prevProps.hasAlertDoc !== nextProps.hasAlertDoc) return false;
  if (prevProps.alertIsOwner !== nextProps.alertIsOwner) return false;
  if (prevProps.alertVisitorMode !== nextProps.alertVisitorMode) return false;
  if (prevProps.onVisitorSubmitGate !== nextProps.onVisitorSubmitGate)
    return false;
  if (prevProps.activeRunSessionId !== nextProps.activeRunSessionId)
    return false;

  return true;
});
